import picaFactory from 'pica';
import { mountImageBatch } from './image-batch-queue.js';
import { canvasToBlob, imageExtension, imageIsAr, imageName, makeCanvas, openBitmap } from './image-batch-utils.js';
import { trackEvent } from '../core/analytics.js';
import { formatBytes } from './extra-common.js';

const t = (ar, en) => imageIsAr ? ar : en;
const AUTO_LABEL = imageIsAr ? 'تلقائي' : 'Auto';

function toStandardDigits(str) {
  return String(str || '')
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));
}

function isAutoDim(val) {
  if (val === undefined || val === null || val === '') return true;
  const s = String(val).trim().toLowerCase();
  return s === 'auto' || s === 'تلقائي' || s === '0';
}

function parseDim(val) {
  if (isAutoDim(val)) return 0;
  const cleaned = toStandardDigits(val).replace(/[^0-9]/g, '');
  const num = parseInt(cleaned, 10);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

const pica = picaFactory({
  tile: 1024,
  concurrency: Math.min(4, typeof navigator !== 'undefined' ? (navigator.hardwareConcurrency || 2) : 2)
});

function getSourceMime(file) {
  const type = (file?.type || '').toLowerCase();
  if (type === 'image/jpeg' || type === 'image/jpg') return 'image/jpeg';
  if (type === 'image/png') return 'image/png';
  if (type === 'image/webp') return 'image/webp';
  if (type === 'image/avif') return 'image/avif';

  const name = (file?.name || '').toLowerCase();
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.avif')) return 'image/avif';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.bmp') || name.endsWith('.ico') || name.endsWith('.gif')) return 'image/png';

  return 'image/jpeg';
}

function getDominantEdgeColor(canvasOrBitmap) {
  try {
    const sampleSize = 32;
    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = sampleSize;
    sampleCanvas.height = sampleSize;
    const sCtx = sampleCanvas.getContext('2d', { willReadFrequently: true });
    if (!sCtx) return '#ffffff';
    sCtx.drawImage(canvasOrBitmap, 0, 0, sampleSize, sampleSize);
    const imgData = sCtx.getImageData(0, 0, sampleSize, sampleSize).data;

    const colorCounts = new Map();
    let maxWeight = 0;
    let dominantRgb = [255, 255, 255];

    for (let y = 0; y < sampleSize; y++) {
      for (let x = 0; x < sampleSize; x++) {
        const idx = (y * sampleSize + x) * 4;
        const a = imgData[idx + 3];
        if (a < 128) continue;

        const r = imgData[idx];
        const g = imgData[idx + 1];
        const b = imgData[idx + 2];

        // Border pixels have higher weight because padding borders touch image edges
        const isBorder = (x === 0 || x === sampleSize - 1 || y === 0 || y === sampleSize - 1);
        const weight = isBorder ? 4 : 1;

        // Quantize colors to reduce variance
        const qr = Math.min(255, Math.round(r / 16) * 16);
        const qg = Math.min(255, Math.round(g / 16) * 16);
        const qb = Math.min(255, Math.round(b / 16) * 16);
        const key = (qr << 16) | (qg << 8) | qb;

        const current = (colorCounts.get(key) || 0) + weight;
        colorCounts.set(key, current);

        if (current > maxWeight) {
          maxWeight = current;
          dominantRgb = [qr, qg, qb];
        }
      }
    }

    const [r, g, b] = dominantRgb;
    return '#' + [r, g, b].map(v => Math.min(255, Math.max(0, v)).toString(16).padStart(2, '0')).join('');
  } catch {
    return '#ffffff';
  }
}

const PRESET_OPTIONS = [
  { value: '1920x1080', label: '1920 × 1080 (Full HD · 16:9)' },
  { value: '1280x720', label: '1280 × 720 (HD · 16:9)' },
  { value: '3840x2160', label: '3840 × 2160 (4K Ultra HD · 16:9)' },
  { value: '1080x1080', label: t('1080 × 1080 (إنستغرام مربع · 1:1)', '1080 × 1080 (Instagram Square · 1:1)') },
  { value: '1080x1920', label: t('1080 × 1920 (ستوري / تيك توك · 9:16)', '1080 × 1920 (Story / Reels / TikTok · 9:16)') },
  { value: '1080x1350', label: t('1080 × 1350 (إنستغرام طولي · 4:5)', '1080 × 1350 (Instagram Portrait · 4:5)') },
  { value: '1200x630', label: t('1200 × 630 (فيسبوك وتويتر · 1.91:1)', '1200 × 630 (Facebook / X Banner · 1.91:1)') },
  { value: '1280x720_yt', label: t('1280 × 720 (غلاف يوتيوب · 16:9)', '1280 × 720 (YouTube Thumbnail · 16:9)') },
  { value: '1200x480', label: t('1200 × 480 (غلاف تويتش · Twitch Banner)', '1200 × 480 (Twitch Header Banner)') },
  { value: '680x240', label: t('680 × 240 (بنر ديسكورد · Discord Banner)', '680 × 240 (Discord Profile Banner)') },
  { value: '512x512', label: t('512 × 512 (أيقونة / صورة شخصية · 1:1)', '512 × 512 (Avatar / Icon · 1:1)') },
  { value: '128x128', label: t('128 × 128 (صورة ديسكورد / إيموجي · 1:1)', '128 × 128 (Discord Avatar / Emoji · 1:1)') },
  { value: '2480x3508', label: t('2480 × 3508 (طباعة A4 بدقة 300 DPI)', '2480 × 3508 (Print A4 · 300 DPI)') },
  { value: '1748x2480', label: t('1748 × 2480 (طباعة A5 بدقة 300 DPI)', '1748 × 2480 (Print A5 · 300 DPI)') },
  { value: '800x600', label: '800 × 600 (Web Standard · 4:3)' }
];

const MAX_EDGE_OPTIONS = [
  { value: '800', label: '800 px' },
  { value: '1280', label: '1280 px (HD)' },
  { value: '1920', label: '1920 px (Full HD)' },
  { value: '2560', label: '2560 px (2K QHD)' },
  { value: '3840', label: '3840 px (4K UHD)' }
];

const fields = [
  {
    key: 'mode',
    label: t('طريقة تغيير الحجم', 'Resize mode'),
    type: 'segmented',
    default: 'percentage',
    span: 'full',
    options: [
      { value: 'percentage', label: t('النسبة المئوية (%)', 'Percentage (%)') },
      { value: 'pixels', label: t('أبعاد بالبكسل (px)', 'Exact pixels (px)') },
      { value: 'preset', label: t('مقاسات جاهزة', 'Presets') },
      { value: 'maxEdge', label: t('أقصى بُعد', 'Max edge') }
    ],
    onChange: ({ value, settings, controls, isGlobal, item }) => {
      if (value === 'pixels') {
        const keepRatio = settings.maintainAspectRatio !== false && settings.maintainAspectRatio !== 'false';
        if (keepRatio) {
          const wCtrl = controls?.get('targetWidth');
          const hCtrl = controls?.get('targetHeight');
          if (wCtrl && hCtrl) {
            if (isGlobal || item?._suppressManualSync || !item?.origWidth) {
              const curW = parseDim(settings.targetWidth || wCtrl.value);
              const curH = parseDim(settings.targetHeight || hCtrl.value);
              if (curW > 0 && curH > 0) {
                hCtrl.value = AUTO_LABEL;
                settings.targetHeight = 'auto';
              } else if (!curW && !curH) {
                wCtrl.value = 1920;
                settings.targetWidth = 1920;
                hCtrl.value = AUTO_LABEL;
                settings.targetHeight = 'auto';
              }
            } else {
              if (!parseDim(wCtrl.value) && !parseDim(hCtrl.value)) {
                wCtrl.value = item.origWidth;
                settings.targetWidth = item.origWidth;
                hCtrl.value = item.origHeight;
                settings.targetHeight = item.origHeight;
              }
            }
          }
        }
      }
    }
  },
  {
    key: 'maintainAspectRatio',
    label: t('الحفاظ على نسبة الأبعاد (تناسب العرض والارتفاع)', 'Maintain aspect ratio'),
    hint: t('تناسب تلقائي يمنع تمطيط أو تشويه الصورة عند تغيير المقاس', 'Preserves original proportions to prevent stretching'),
    type: 'checkbox',
    default: true,
    span: 'full',
    onChange: ({ value, settings, controls, isGlobal, item }) => {
      if (!value || settings.mode !== 'pixels') return;
      const wCtrl = controls?.get('targetWidth');
      const hCtrl = controls?.get('targetHeight');
      if (!wCtrl || !hCtrl) return;

      const numW = parseDim(settings.targetWidth || wCtrl.value);
      const numH = parseDim(settings.targetHeight || hCtrl.value);

      if (isGlobal || item?._suppressManualSync || !item?.origWidth) {
        if (numW > 0) {
          hCtrl.value = AUTO_LABEL;
          settings.targetHeight = 'auto';
        } else if (numH > 0) {
          wCtrl.value = AUTO_LABEL;
          settings.targetWidth = 'auto';
        } else {
          wCtrl.value = 1920;
          settings.targetWidth = 1920;
          hCtrl.value = AUTO_LABEL;
          settings.targetHeight = 'auto';
        }
      } else {
        if (numW > 0) {
          const newH = Math.max(1, Math.round(numW * (item.origHeight / item.origWidth)));
          hCtrl.value = newH;
          settings.targetHeight = newH;
        } else if (numH > 0) {
          const newW = Math.max(1, Math.round(numH * (item.origWidth / item.origHeight)));
          wCtrl.value = newW;
          settings.targetWidth = newW;
        } else {
          wCtrl.value = item.origWidth;
          settings.targetWidth = item.origWidth;
          hCtrl.value = item.origHeight;
          settings.targetHeight = item.origHeight;
        }
      }
    }
  },
  {
    key: 'canvasPadding',
    label: t('تطبيق المقاس على الكانفاس (الحفاظ على أبعاد الصورة)', 'Apply size to canvas (keep image proportions)'),
    hint: t('توسيط الصورة بأبعادها الأصلية وملء المساحة الإضافية (شفاف في PNG ولون الصورة في JPG)', 'Preserves image proportions, centers it, and pads extra space (transparent for PNG, dominant image color for JPG)'),
    type: 'checkbox',
    default: false,
    span: 'full',
    showIf: (s) => (s.maintainAspectRatio === false || s.maintainAspectRatio === 'false')
  },
  // 1. Percentage Mode Controls
  {
    key: 'scale',
    label: t('نسبة الحجم', 'Scale percentage'),
    type: 'segmented',
    default: '100',
    span: 'full',
    options: [
      { value: '25', label: '25%' },
      { value: '50', label: '50%' },
      { value: '75', label: '75%' },
      { value: '100', label: '100%' },
      { value: '125', label: '125%' },
      { value: '150', label: '150%' },
      { value: '200', label: '200%' },
      { value: 'custom', label: t('مخصص…', 'Custom…') }
    ],
    showIf: (s) => (s.mode || 'percentage') === 'percentage'
  },
  {
    key: 'customScale',
    label: t('النسبة المئوية المخصصة', 'Custom scale'),
    type: 'number',
    default: 80,
    min: 1,
    max: 1000,
    step: 1,
    suffix: '%',
    placeholder: '80',
    showIf: (s) => (s.mode || 'percentage') === 'percentage' && s.scale === 'custom'
  },
  // 2. Pixels Mode Controls
  {
    key: 'targetWidth',
    label: t('العرض المطلوب', 'Target width'),
    type: 'text',
    default: 1920,
    suffix: 'px',
    placeholder: t('1920 أو تلقائي', '1920 or Auto'),
    showIf: (s) => s.mode === 'pixels',
    onChange: ({ value, settings, controls, isGlobal, item }) => {
      const keepRatio = settings.maintainAspectRatio !== false && settings.maintainAspectRatio !== 'false';
      if (!keepRatio) return;

      const hCtrl = controls?.get('targetHeight');
      if (!hCtrl) return;

      if (isAutoDim(value)) {
        const curH = parseDim(settings.targetHeight);
        if (!curH) {
          const fallbackH = item?.origHeight || 1080;
          hCtrl.value = fallbackH;
          settings.targetHeight = fallbackH;
        }
        return;
      }

      const numW = parseDim(value);
      if (!numW) return;

      if (isGlobal || item?._suppressManualSync || !item?.origWidth) {
        hCtrl.value = AUTO_LABEL;
        settings.targetHeight = 'auto';
      } else {
        const newH = Math.max(1, Math.round(numW * (item.origHeight / item.origWidth)));
        hCtrl.value = newH;
        settings.targetHeight = newH;
      }
    }
  },
  {
    key: 'targetHeight',
    label: t('الارتفاع المطلوب', 'Target height'),
    type: 'text',
    default: AUTO_LABEL,
    suffix: 'px',
    placeholder: t('تلقائي أو بكسل', 'Auto or px'),
    showIf: (s) => s.mode === 'pixels',
    onChange: ({ value, settings, controls, isGlobal, item }) => {
      const keepRatio = settings.maintainAspectRatio !== false && settings.maintainAspectRatio !== 'false';
      if (!keepRatio) return;

      const wCtrl = controls?.get('targetWidth');
      if (!wCtrl) return;

      if (isAutoDim(value)) {
        const curW = parseDim(settings.targetWidth);
        if (!curW) {
          const fallbackW = item?.origWidth || 1920;
          wCtrl.value = fallbackW;
          settings.targetWidth = fallbackW;
        }
        return;
      }

      const numH = parseDim(value);
      if (!numH) return;

      if (isGlobal || item?._suppressManualSync || !item?.origWidth) {
        wCtrl.value = AUTO_LABEL;
        settings.targetWidth = 'auto';
      } else {
        const newW = Math.max(1, Math.round(numH * (item.origWidth / item.origHeight)));
        wCtrl.value = newW;
        settings.targetWidth = newW;
      }
    }
  },
  // 3. Preset Mode Controls
  {
    key: 'preset',
    label: t('المقاس القياسي الجاهز', 'Preset dimensions'),
    type: 'select',
    default: '1920x1080',
    options: PRESET_OPTIONS,
    span: 'full',
    showIf: (s) => s.mode === 'preset'
  },
  // 4. Max Edge Mode Controls
  {
    key: 'maxEdge',
    label: t('أقصى طول للضلع', 'Max edge length'),
    type: 'segmented',
    default: '1920',
    span: 'full',
    options: MAX_EDGE_OPTIONS,
    showIf: (s) => s.mode === 'maxEdge'
  },
  // Format & Quality
  {
    key: 'format',
    label: t('صيغة الإخراج', 'Output format'),
    type: 'select',
    default: 'original',
    options: [
      { value: 'original', label: t('نفس الصيغة الأصلية (تلقائي)', 'Original (Same as source)') },
      { value: 'image/webp', label: 'WebP' },
      { value: 'image/avif', label: 'AVIF' },
      { value: 'image/jpeg', label: 'JPEG' },
      { value: 'image/png', label: 'PNG' }
    ]
  },
  {
    key: 'quality',
    label: t('جودة الصورة %', 'Image Quality %'),
    type: 'range',
    min: 10,
    max: 100,
    step: 1,
    default: 100,
    suffix: '%',
    showIf: (s) => (s.format || 'original') !== 'image/png'
  }
];


function calculateTargetDims(origW, origH, settings) {
  const oW = Number(origW) || 1;
  const oH = Number(origH) || 1;
  const keepRatio = settings.maintainAspectRatio !== false && settings.maintainAspectRatio !== 'false';
  const mode = settings.mode || 'percentage';

  let width = oW;
  let height = oH;

  if (mode === 'percentage') {
    const scaleVal = settings.scale === 'custom'
      ? (Number(settings.customScale) || 80)
      : (Number(settings.scale) || 100);
    const scale = Math.max(0.01, scaleVal / 100);
    width = Math.max(1, Math.round(oW * scale));
    height = Math.max(1, Math.round(oH * scale));
  } else if (mode === 'pixels') {
    const reqW = parseDim(settings.targetWidth);
    const reqH = parseDim(settings.targetHeight);
    if (reqW > 0 && reqH > 0) {
      if (keepRatio) {
        const scale = Math.min(reqW / oW, reqH / oH);
        width = Math.max(1, Math.round(oW * scale));
        height = Math.max(1, Math.round(oH * scale));
      } else {
        width = Math.max(1, Math.round(reqW));
        height = Math.max(1, Math.round(reqH));
      }
    } else if (reqW > 0) {
      width = Math.max(1, Math.round(reqW));
      height = keepRatio ? Math.max(1, Math.round(oH * (reqW / oW))) : oH;
    } else if (reqH > 0) {
      height = Math.max(1, Math.round(reqH));
      width = keepRatio ? Math.max(1, Math.round(oW * (reqH / oH))) : oW;
    } else {
      width = oW;
      height = oH;
    }
  } else if (mode === 'preset') {
    const presetKey = settings.preset || '1920x1080';
    const [pW, pH] = presetKey.split('_')[0].split('x').map(Number);
    if (keepRatio) {
      const scale = Math.min(pW / oW, pH / oH);
      width = Math.max(1, Math.round(oW * scale));
      height = Math.max(1, Math.round(oH * scale));
    } else {
      width = pW;
      height = pH;
    }
  } else if (mode === 'maxEdge') {
    const maxDim = Number(settings.maxEdge) || 1920;
    const currentMax = Math.max(oW, oH);
    if (keepRatio) {
      const scale = currentMax > 0 ? maxDim / currentMax : 1;
      width = Math.max(1, Math.round(oW * scale));
      height = Math.max(1, Math.round(oH * scale));
    } else {
      width = maxDim;
      height = maxDim;
    }
  }

  if (!Number.isFinite(width) || width <= 0) width = oW;
  if (!Number.isFinite(height) || height <= 0) height = oH;

  return { width, height };
}

let resizerPreviewSeq = 0;

mountImageBatch({
  rootId: 'resizer-app',
  fields,
  zipName: 'resized-images.zip',
  renderPreview: ({ canvas, ctx, bitmap, settings, item, statsEl }) => {
    const origW = bitmap.width;
    const origH = bitmap.height;
    const { width, height } = calculateTargetDims(origW, origH, settings);

    const resolvedMime = settings.format === 'original'
      ? getSourceMime(item?.file)
      : (settings.format || 'image/webp');
    const isJpeg = resolvedMime === 'image/jpeg';
    const keepRatio = settings.maintainAspectRatio !== false && settings.maintainAspectRatio !== 'false';
    const useCanvasPadding = !keepRatio && Boolean(settings.canvasPadding);

    const containerW = canvas.parentElement?.clientWidth || 720;
    const maxStageW = Math.min(760, Math.max(280, containerW - 24));
    const maxStageH = 460;

    // Visual scale factor for preview stage:
    // The target canvas aspect ratio is strictly width / height.
    const fitScale = Math.min(maxStageW / width, maxStageH / height);

    let displayW, displayH;
    const maxTargetDim = Math.max(width, height);
    if (maxTargetDim < 80) {
      // Small images: give minimal visible size of 80px while strictly preserving aspect ratio
      const upScale = Math.min(80 / maxTargetDim, maxStageW / width, maxStageH / height);
      displayW = Math.max(1, Math.round(width * upScale));
      displayH = Math.max(1, Math.round(height * upScale));
    } else if (fitScale < 1) {
      // Large images: scale down proportionally to fit the viewer
      displayW = Math.max(1, Math.round(width * fitScale));
      displayH = Math.max(1, Math.round(height * fitScale));
    } else {
      // Fits 1:1 comfortably
      displayW = width;
      displayH = height;
    }

    canvas.style.width = `${displayW}px`;
    canvas.style.height = `${displayH}px`;
    canvas.width = displayW;
    canvas.height = displayH;

    let dominantBgColor = '#ffffff';

    if (useCanvasPadding) {
      // Contain the image within displayW x displayH while maintaining origW / origH
      const imgScale = Math.min(displayW / origW, displayH / origH);
      const drawW = Math.max(1, Math.round(origW * imgScale));
      const drawH = Math.max(1, Math.round(origH * imgScale));
      const drawX = Math.round((displayW - drawW) / 2);
      const drawY = Math.round((displayH - drawH) / 2);

      if (isJpeg) {
        dominantBgColor = getDominantEdgeColor(bitmap);
        ctx.fillStyle = dominantBgColor;
        ctx.fillRect(0, 0, displayW, displayH);
      } else {
        ctx.clearRect(0, 0, displayW, displayH);
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, drawX, drawY, drawW, drawH);
    } else {
      if (isJpeg) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, displayW, displayH);
      } else {
        ctx.clearRect(0, 0, displayW, displayH);
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, 0, 0, displayW, displayH);
    }

    const fmt = settings.format === 'original'
      ? (resolvedMime.split('/')[1]?.toUpperCase() || 'AUTO')
      : (settings.format || 'image/webp').split('/')[1]?.toUpperCase() || 'WEBP';
    const pctW = Math.round((width / origW) * 100);
    const qualPercent = Math.max(10, Math.min(100, Number(settings.quality) || 100));
    const isLossless = resolvedMime === 'image/png';
    const qualText = !isLossless ? ` · Q:${qualPercent}%` : '';
    const padBadge = useCanvasPadding ? (imageIsAr ? ' · كانفاس موسّع' : ' · Padded Canvas') : '';

    if (statsEl) {
      statsEl.textContent = `${origW} × ${origH} px → ${width} × ${height} px (${pctW}% · ${fmt}${qualText}${padBadge})`;
    }

    // Live lossy quality simulation
    if (!isLossless) {
      const renderSeq = ++resizerPreviewSeq;
      const targetMime = isJpeg ? 'image/jpeg' : (resolvedMime === 'image/avif' ? 'image/webp' : 'image/webp');
      const qual = qualPercent / 100;
      canvas.toBlob(async (blob) => {
        if (!blob || renderSeq !== resizerPreviewSeq) return;
        try {
          const compBmp = await createImageBitmap(blob);
          if (renderSeq !== resizerPreviewSeq) {
            compBmp.close();
            return;
          }
          if (isJpeg) {
            ctx.fillStyle = useCanvasPadding ? dominantBgColor : '#ffffff';
            ctx.fillRect(0, 0, displayW, displayH);
          } else {
            ctx.clearRect(0, 0, displayW, displayH);
          }
          if (useCanvasPadding) {
            const imgScale = Math.min(displayW / origW, displayH / origH);
            const drawW = Math.max(1, Math.round(origW * imgScale));
            const drawH = Math.max(1, Math.round(origH * imgScale));
            const drawX = Math.round((displayW - drawW) / 2);
            const drawY = Math.round((displayH - drawH) / 2);
            ctx.drawImage(compBmp, drawX, drawY, drawW, drawH);
          } else {
            ctx.drawImage(compBmp, 0, 0, displayW, displayH);
          }
          compBmp.close();

          const bytesPerPx = blob.size / Math.max(1, displayW * displayH);
          const estTargetBytes = Math.round(bytesPerPx * (width * height));
          if (statsEl) {
            statsEl.textContent = `${origW} × ${origH} px → ${width} × ${height} px · ~${formatBytes(estTargetBytes)} (${pctW}% · ${fmt}${qualText}${padBadge})`;
          }
        } catch {
          // ignore
        }
      }, targetMime, qual);
    }
  },
  process: async (file, settings, report) => {
    report(8, t('جارٍ فتح الصورة', 'Opening image'));
    const bitmap = await openBitmap(file);
    const origW = bitmap.width;
    const origH = bitmap.height;
    const { width, height } = calculateTargetDims(origW, origH, settings);

    if (width * height > 35_000_000) {
      bitmap.close();
      throw new Error(t('المقاس الناتج يتجاوز 35 ميجابكسل. يرجى اختيار أبعاد أصغر.', 'The output exceeds 35 megapixels. Please choose smaller dimensions.'));
    }

    const resolvedMime = settings.format === 'original'
      ? getSourceMime(file)
      : (settings.format || 'image/webp');
    const isJpeg = resolvedMime === 'image/jpeg';
    const keepRatio = settings.maintainAspectRatio !== false && settings.maintainAspectRatio !== 'false';
    const useCanvasPadding = !keepRatio && Boolean(settings.canvasPadding);

    const source = document.createElement('canvas');
    source.width = origW;
    source.height = origH;
    source.getContext('2d').drawImage(bitmap, 0, 0);
    bitmap.close();

    report(28, t('جارٍ تغيير الأبعاد بجودة فائقة', 'Resizing at high quality'));

    let targetCanvas;
    let targetCtx;

    if (useCanvasPadding) {
      // Preserve image proportions with contain fit inside target width x height
      const scale = Math.min(width / origW, height / origH);
      const drawW = Math.max(1, Math.round(origW * scale));
      const drawH = Math.max(1, Math.round(origH * scale));
      const offsetX = Math.round((width - drawW) / 2);
      const offsetY = Math.round((height - drawH) / 2);

      const scaledCanvas = document.createElement('canvas');
      scaledCanvas.width = drawW;
      scaledCanvas.height = drawH;
      try {
        await pica.resize(source, scaledCanvas, { quality: 3, unsharpAmount: 70, unsharpRadius: 0.6, unsharpThreshold: 2 });
      } catch {
        const sCtx = scaledCanvas.getContext('2d');
        sCtx.imageSmoothingEnabled = true;
        sCtx.imageSmoothingQuality = 'high';
        sCtx.drawImage(source, 0, 0, drawW, drawH);
      }

      let bgColor = null;
      if (isJpeg) {
        bgColor = getDominantEdgeColor(source);
      }
      const created = makeCanvas(width, height, bgColor);
      targetCanvas = created.canvas;
      targetCtx = created.context;

      targetCtx.drawImage(scaledCanvas, offsetX, offsetY);
    } else {
      const { canvas, context } = makeCanvas(width, height, isJpeg ? '#ffffff' : null);
      targetCanvas = canvas;
      targetCtx = context;
      try {
        await pica.resize(source, targetCanvas, { quality: 3, unsharpAmount: 70, unsharpRadius: 0.6, unsharpThreshold: 2 });
      } catch {
        targetCtx.drawImage(source, 0, 0, width, height);
      }
    }

    report(70, t('جارٍ تجهيز الصورة وتصديرها', 'Preparing and exporting'));
    let blob;
    const quality = Number(settings.quality ?? 100) / 100;
    try {
      blob = await canvasToBlob(targetCanvas, resolvedMime, quality);
    } catch {
      blob = await canvasToBlob(targetCanvas, 'image/png');
    }

    const ext = imageExtension(blob.type);
    const mode = settings.mode || 'percentage';
    report(100, t('اكتمل تغيير الحجم بنجاح', 'Resize complete'));
    trackEvent('tool_completed', 'batch-image-resizer', { width, height, format: ext, mode, keepRatio, canvasPadding: useCanvasPadding });

    const ratioLabel = keepRatio
      ? t('مع تناسب الأبعاد', 'ratio locked')
      : (useCanvasPadding ? t('توسيط على الكانفاس', 'padded canvas') : t('أبعاد حرة', 'custom ratio'));
    const finalFilename = `${imageName(file.name)}.${ext}`;
    return {
      blob,
      filename: finalFilename,
      details: `${width} × ${height} px · ${(blob.size / 1024).toFixed(0)} KB · ${ratioLabel}`
    };
  }
});
